import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import StepTrim, { fmtClip } from '../portal/composer/StepTrim';
import { MAX_MESSAGE_CLIP_MS as MAX_CLIP_MS } from '../../services/messageService';
import useVideoFrames from '../portal/composer/useVideoFrames';
import { SheetCta } from '../portal/composer/Sheet';

/**
 * The trim gate for a video message — the Portal composer's TRIM step, on black, over the
 * thread. A clip up to 15 min (`MAX_MESSAGE_CLIP_MS`; Portals keep 30 s) goes straight through;
 * anything longer lands here with its first 15 min selected, and the window can be dragged
 * anywhere in the file. The strip grows more frames for a long file so the handles have
 * something to land on. The preview plays the window on a loop so what you see is what gets sent.
 *
 * Nothing is uploaded from here: it hands back `{ startMs, endMs }` and the composer sends.
 */
export default function VideoTrimSheet({ file, localUrl, onConfirm, onCancel }) {
  const [trim, setTrim] = useState({ startMs: 0, endMs: 0 });
  const [playSec, setPlaySec] = useState(0);
  const [attempted, setAttempted] = useState(false);
  const { frames, durationSec, extracting } = useVideoFrames(localUrl, 'video', trim);
  const videoRef = useRef(null);

  // A fresh file opens on its first 15 minutes.
  useEffect(() => {
    if (!durationSec) return;
    const ms = Math.round(durationSec * 1000);
    setTrim((t) => (t.endMs > t.startMs ? t : { startMs: 0, endMs: Math.min(ms, MAX_CLIP_MS) }));
  }, [durationSec]);

  const wasExtracting = useRef(false);
  useEffect(() => {
    if (wasExtracting.current && !extracting) setAttempted(true);
    wasExtracting.current = extracting;
  }, [extracting]);
  const failed = attempted && !extracting && !durationSec;

  // The preview loops inside the window; a drag scrubs it.
  const onTrim = (next, { scrubToMs } = {}) => {
    setTrim(next);
    const v = videoRef.current;
    if (v && scrubToMs != null) { try { v.currentTime = scrubToMs / 1000; } catch { /* not seekable yet */ } }
  };
  useEffect(() => {
    const v = videoRef.current;
    if (!v || !(trim.endMs > trim.startMs)) return undefined;
    const onTime = () => {
      setPlaySec(v.currentTime);
      if (v.currentTime >= trim.endMs / 1000 - 0.06 || v.currentTime < trim.startMs / 1000 - 0.1) {
        try { v.currentTime = trim.startMs / 1000; } catch { /* not seekable yet */ }
      }
    };
    v.addEventListener('timeupdate', onTime);
    v.play?.().catch(() => {});
    return () => v.removeEventListener('timeupdate', onTime);
  }, [trim.startMs, trim.endMs]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onCancel?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCancel]);

  const clipMs = trim.endMs - trim.startMs;
  const node = (
    <div data-testid="video-trim-sheet" role="dialog" aria-modal="true" aria-label="Trim your clip" className="pr-overlay-root font-mono" style={{ position: 'fixed', inset: 0, zIndex: 2000, background: '#000', color: '#fff', display: 'flex', flexDirection: 'column', paddingTop: 'env(safe-area-inset-top)', paddingBottom: 'env(safe-area-inset-bottom)' }}>
      <div style={{ display: 'grid', gridTemplateColumns: '56px minmax(0,1fr) 56px', alignItems: 'center', padding: '14px 16px 0' }}>
        <button type="button" onClick={onCancel} aria-label="Cancel" style={{ width: 40, height: 40, borderRadius: '50%', border: 0, background: 'rgba(255,255,255,.12)', color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="square"><path d="M6 18L18 6M6 6l12 12" /></svg>
        </button>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 14, fontWeight: 700, letterSpacing: '0.14em' }}>TRIM YOUR CLIP</div>
          <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.14em', color: 'rgba(255,255,255,.7)', marginTop: 4 }}>
            {durationSec ? `${fmtClip(durationSec)} · UP TO ${fmtClip(MAX_CLIP_MS / 1000)} GOES` : 'READING THE FILE…'}
          </div>
        </div>
        <span />
      </div>
      <div style={{ flex: 1, minHeight: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
        {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
        <video ref={videoRef} src={localUrl} muted playsInline loop={false} style={{ maxWidth: '100%', maxHeight: '100%', borderRadius: 16, background: '#111' }} />
      </div>
      <div style={{ padding: '0 16px 16px', display: 'flex', flexDirection: 'column', gap: 12 }}>
        <StepTrim frames={frames} durationSec={durationSec} trim={trim} onTrim={onTrim} maxClipMs={MAX_CLIP_MS} playSec={playSec} loading={extracting && !durationSec} failed={failed} />
        <SheetCta data-testid="video-trim-confirm" onClick={() => onConfirm({ startMs: Math.round(trim.startMs), endMs: Math.round(trim.endMs) })} disabled={failed || !(clipMs > 0)}>
          {clipMs > 0 ? `USE THESE ${fmtClip(clipMs / 1000)}` : 'USE THIS CLIP'}
        </SheetCta>
      </div>
    </div>
  );
  return createPortal(node, document.body);
}
